import { useInView, motion } from "motion/react";
import className from "classnames/bind";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { RhLink, Image } from "@components";
import { GetAllInducteesQuery } from "@/__generated__/graphql";
import { AnimVals } from "@/utils/animUtils";

import styles from "./InducteesAToZ.module.scss";
import { removeHyphens } from "@/core/utils";
const cx = className.bind(styles);

type Inductee = GetAllInducteesQuery["allInductees"][number];

const InducteesAToZItem = forwardRef<
  { getInnerRef: () => HTMLAnchorElement | null },
  Inductee
>((props: Inductee, ref) => {
  const {
    uri,
    title,
    pageData: { featuredImage },
    inducteeData: { inducteeClass, category },
  } = props;
  const innerRef = useRef<HTMLAnchorElement>(null);
  const inView = useInView(innerRef, { once: AnimVals.once });
  const [loadImage, setLoadImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const inducteeCategory = useMemo(() => {
    return removeHyphens(category[0] || "");
  }, [category]);
  const onImageLoad = () => setImageLoaded(true);
  useImperativeHandle(ref, () => ({
    // You can expose other methods or properties of innerRef here
    getInnerRef: () => innerRef.current,
  }));
  useEffect(() => {
    if (inView && featuredImage?.node?.mediaItemUrl && !loadImage) {
      setLoadImage(true);
    }
  }, [inView, featuredImage?.node?.mediaItemUrl]);
  return (
    <RhLink href={uri} key={title} className={cx("inductee-item")} ref={innerRef}>
      <div className={cx(["image-wrapper", { "image-loaded": imageLoaded }])}>
        <span className={cx(["placeholder"])} />
        {loadImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Image
              image={featuredImage}
              onLoad={onImageLoad}
              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 25vw, 15vw"
              className={cx("image")}
            />
          </motion.div>
        )}
      </div>
      <div className={cx("copy")}>
        <p className={cx("name", "text-m")}>{title}</p>
        <p className={cx("pill", "class", "MD-TagSmall")}>
          {inducteeClass[0]?.slug || "Unknown Class"}
        </p>
        <p className={cx("pill", "class", "MD-TagSmall")}>
          {inducteeCategory}
        </p>
      </div>
    </RhLink>
  );
});

export default InducteesAToZItem;
